import React, { useState } from 'react';
import { useLazyGetAccountsQuery, useLazyGetHoldingsQuery } from 'src/api/atv/atvApi';
import FastLink from 'src/components/common/Yodlee/FastLink';
import StepHeader from 'src/features/transfer/components/StepHeader/StepHeader';
import { useYodleeInit } from 'src/features/transfer/hooks/useYodleeInit';

const getRandomValue = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const obfuscatePII = (data: any[]) => {
  return data.map((item) => {
    const obfuscatedHoldings = item.holdings?.map((holding: any) => {
      const quantity = getRandomValue(1, 100);
      const price = getRandomValue(10, 500);
      const value = quantity * price;

      return {
        ...holding,
        quantity: quantity,
        price: { ...(holding.price ?? {}), amount: price },
        value: { ...(holding.value ?? {}), amount: value },
        costBasis: { ...(holding.costBasis ?? {}), amount: getRandomValue(50, 100) },
      };
    });

    const cashValue = getRandomValue(1000, 5000);
    const totalValue =
      (obfuscatedHoldings?.reduce(
        (acc: any, holding: any) => acc + holding.value.amount,
        0,
      ) ?? 0) + cashValue;

    const obfuscatedAccount = {
      ...item.account,
      accountName: 'hidden',
      displayedName: 'hidden',
      accountNumber: 'hidden',
      fullAccountNumberList: { unmaskedAccountNumber: 'hidden' },
      balance: { ...(item.account.balance ?? {}), amount: totalValue },
      cash: { ...(item.account.cash ?? {}), amount: cashValue },
      holder: [
        {
          name: {
            first: 'hidden',
            last: 'hidden',
            middle: 'hidden',
            fullName: 'hidden',
          },
        },
      ],
    };

    return { account: obfuscatedAccount, holdings: obfuscatedHoldings };
  });
};

const DataCollection = () => {
  const { isFastLinkInitialized, yodleeData, YodleeUrl, configName, obAppName } =
    useYodleeInit();
  const [getAccountsQuery] = useLazyGetAccountsQuery();
  const [getHoldingsQuery] = useLazyGetHoldingsQuery();

  const [finalJson, setFinalJson] = useState<any[] | null>(null);
  const [copyStatusMessage, setCopyStatusMessage] = useState('');

  const onSuccessFastlink = async (data: any) => {
    const accountsData = await getAccountsQuery({
      uuid: yodleeData?.id ?? '',
      providerAccountID: data?.providerAccountId,
    });

    const promises = accountsData.data?.map((account: any) => {
      return getHoldingsQuery({
        uuid: yodleeData?.id ?? '',
        providerAccountID: data?.providerAccountId,
        accountID: account.id,
      });
    });

    const holdings = (await Promise.all(promises ?? [])).map((res) => res.data);
    const combinedData = Array.isArray(accountsData.data)
      ? accountsData.data?.map((account: any, index: number) => {
          return { account: JSON.parse(account.rawData), holdings: holdings[index] };
        })
      : [];
    setFinalJson(obfuscatePII(combinedData ?? []));
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(JSON.stringify(finalJson, null, 2))
      .then(() => {
        setCopyStatusMessage('Copied!');
      })
      .catch(() => {
        setCopyStatusMessage('Failed to copy!');
      });
  };

  return (
    <main>
      <section className="layout-section">
        <StepHeader text="Yodlee Test Data Collection Tool" />
        <div className="content-container">
          <div className="content-left" style={{ width: '800px' }}>
            {!finalJson && (
              <div>
                <div className="b-typography-sans-serif--headline--50 b-font-weight-bold">
                  Search for your current financial firm
                </div>
                {isFastLinkInitialized && yodleeData && (
                  <FastLink
                    fastLinkURL={yodleeData?.fastLinkUrl ?? ''}
                    accessToken={yodleeData?.token ?? ''}
                    YodleeUrl={YodleeUrl}
                    configName={configName}
                    obAppName={obAppName}
                    onSuccess={onSuccessFastlink}
                  />
                )}
              </div>
            )}
            {finalJson && (
              <>
                <div className="b-typography-sans-serif--headline--50 b-font-weight-bold">
                  Your JSON is Ready
                </div>
                <div className="b-typography-sans-serif--body--50">
                  <button onClick={copyToClipboard}>Copy JSON to Clipboard</button>
                  {copyStatusMessage && <p>{copyStatusMessage}</p>}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default DataCollection;
